import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useId, useState } from "react";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

import { useProPlanLabels } from "../../lib/useProPlanLabels";
import { CHECKOUT_WAYS, type CheckoutWay } from "./CheckoutWay";

type CheckoutChooseProps = {
  pending: boolean;
  error: ReactNode;
  onSubmit: (way: CheckoutWay) => void;
};

// Выбор способа оплаты и согласие с соглашением (design/prototype/ProCheckout.dc.html).
// Соглашение открывается в новой вкладке, чтобы окно оплаты не закрылось.
// Галочка не стоит заранее; для автоплатежа в ней же — согласие на регулярные списания.
export const CheckoutChoose = ({ pending, error, onSubmit }: CheckoutChooseProps) => {
  const { t } = useLingui();
  const { price, period } = useProPlanLabels();
  const [way, setWay] = useState<CheckoutWay>("autopay");
  const [agree, setAgree] = useState(false);
  const groupName = useId();

  const labels: Record<CheckoutWay, { name: string; hint: string }> = {
    autopay: {
      name: t`Autopay via SBP`,
      hint: t`${price} every ${period}. You can turn it off at any time.`,
    },
    sbp: { name: t`One-time via SBP`, hint: t`Pro for ${period}, without renewal.` },
    card: { name: t`One-time by card`, hint: t`Pro for ${period}, without renewal.` },
  };

  return (
    <form
      className="flex flex-col gap-4.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (agree) {
          onSubmit(way);
        }
      }}
    >
      <fieldset className="flex flex-col gap-2.5">
        <legend className="sr-only">
          <Trans>How to pay</Trans>
        </legend>
        {CHECKOUT_WAYS.map((option) => (
          <label
            key={option}
            className={cn(
              "grid min-h-16 cursor-pointer grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-0.5 rounded-[10px] border px-3.5 py-3",
              way === option
                ? "border-accent bg-surface-raised"
                : "border-border bg-background hover:border-border-strong",
            )}
          >
            <input
              type="radio"
              name={groupName}
              value={option}
              checked={way === option}
              onChange={() => {
                setWay(option);
              }}
              className="row-span-2 size-4.5 shrink-0 accent-accent"
            />
            <span className="font-semibold">{labels[option].name}</span>
            <span className="text-[13px] text-muted">{labels[option].hint}</span>
          </label>
        ))}
      </fieldset>

      <label className="flex cursor-pointer items-start gap-2.5 text-sm text-foreground-soft">
        <input
          type="checkbox"
          checked={agree}
          onChange={(event) => {
            setAgree(event.target.checked);
          }}
          className="mt-0.5 size-5 shrink-0 accent-accent"
        />
        <span>
          {way === "autopay" ? (
            <Trans>
              I accept the{" "}
              <a href="/terms" target="_blank" className="text-accent hover:text-accent-strong">
                user agreement and offer
              </a>{" "}
              and agree to be charged {price} every {period}
            </Trans>
          ) : (
            <Trans>
              I accept the{" "}
              <a href="/terms" target="_blank" className="text-accent hover:text-accent-strong">
                user agreement and offer
              </a>
            </Trans>
          )}
        </span>
      </label>

      {error}

      <Button
        type="submit"
        size="lg"
        disabled={!agree}
        loading={pending}
        className="h-13 text-base font-semibold"
      >
        {pending ? (
          <Trans>Preparing payment…</Trans>
        ) : way === "autopay" ? (
          <Trans>Turn on autopay · {price}</Trans>
        ) : (
          <Trans>Go to payment · {price}</Trans>
        )}
      </Button>
      <p className="text-center text-[13px] text-subtle">
        <Trans>
          Payments are accepted by the payment service Platega. We never see card details.
        </Trans>
      </p>
    </form>
  );
};
